import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'universal-cookie';

import { reinitializeInstance } from '@common/apis/baseApi';
import { LoginLoader } from '@common/components/loader/login-loader';
import { login } from '@common/components/authentication/auth.slice';
import { checkLogin } from '@common/components/authentication/auth.thunks';
import { getAppConfig } from '@common/apis/users';
import { RootState, AppDispatch } from '@store/types';
import { setOrg } from '@store/org.slice';

export const PartnerProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isLoggedIn, isLoading, isTeamsApp } = useSelector((state: RootState) => state.auth);
  const [checkPerformed, setCheckPerformed] = useState(false);

  const handleTokensFromUrl = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const accessToken = searchParams.get('x-access-token');
    const expiry = searchParams.get('expiry');

    if (accessToken && expiry) {
      const cookies = new Cookies();
      const cookieOptions = { path: '/', sameSite: 'none' as const, secure: true };
      cookies.set('x-access-token', accessToken, { ...cookieOptions, maxAge: 3600 });
      cookies.set('expiry', expiry, { ...cookieOptions, maxAge: 7200 });
      reinitializeInstance();
    }
  };

  const initializeAuth = async () => {
    try {
      const result = await dispatch(checkLogin()).unwrap();
      if (result) {
        dispatch(login(result));
      }
      const appConfig = await getAppConfig();
      const organization = appConfig?.data?.organization;
      if (organization) {
        dispatch(setOrg(organization));
      }
    } catch {
      // not logged in — handled by isLoggedIn check below
    } finally {
      setCheckPerformed(true);
    }
  };

  useEffect(() => {
    handleTokensFromUrl();
    initializeAuth(); // eslint-disable-line react-hooks/exhaustive-deps
  }, [dispatch, isTeamsApp]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !checkPerformed) {
    return <LoginLoader />;
  }

  if (!isLoggedIn) {
    const currentPath = window.location.pathname + window.location.search + window.location.hash;
    localStorage.setItem('sbrain_next_url', currentPath);
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};
